import React, { useState } from "react";
import { createPortal } from "react-dom";
import { X, User } from "lucide-react";
import { getEmoji } from "../../utils/communityReactions";

const ReactionModal = ({ isOpen, onClose, post }) => {
  const [activeTab, setActiveTab] = useState("All");

  if (!isOpen || !post) return null;

  // Group reactions
  const reactions = post.reactions || [];
  const groupedReactions = reactions.reduce((acc, curr) => {
    acc[curr.type] = (acc[curr.type] || 0) + 1;
    return acc;
  }, {});

  // Get unique reaction types that are actually used
  const availableTypes = Object.keys(groupedReactions).sort((a, b) => groupedReactions[b] - groupedReactions[a]);
  const tabs = ["All", ...availableTypes];

  const filteredReactions = activeTab === "All" 
    ? reactions 
    : reactions.filter(r => r.type === activeTab);

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[85vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Reactions</h2>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex px-4 pt-2 border-b border-gray-100 gap-6 overflow-x-auto scrollbar-hide">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex items-center gap-1.5 pb-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                activeTab === tab
                  ? "border-[#1a3884] text-[#1a3884]"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <span className="text-base">{tab === "All" ? "All" : getEmoji(tab)}</span>
              <span className="text-xs">
                {tab === "All" ? reactions.length : groupedReactions[tab]}
              </span>
            </button>
          ))}
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto p-2">
          {filteredReactions.length > 0 ? (
            <div className="space-y-1">
              {filteredReactions.map((reaction, idx) => (
                <div key={idx} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg transition-colors">
                  <div className="relative">
                    {reaction.user?.profileImage ? (
                      <img 
                        src={reaction.user.profileImage} 
                        alt={reaction.user.fullName} 
                        className="w-10 h-10 rounded-full object-cover border border-gray-100"
                      />
                    ) : (
                      <div className="w-10 h-10 bg-[#1a3884]/10 rounded-full flex items-center justify-center text-[#1a3884] font-bold border border-[#1a3884]/20">
                        {reaction.user?.fullName?.[0] || <User size={18} />}
                      </div>
                    )}
                    <div className="absolute -bottom-1 -right-1 w-[22px] h-[22px] bg-white rounded-full flex items-center justify-center shadow-sm border border-gray-100 text-[12px] leading-none z-10">
                      {getEmoji(reaction.type)}
                    </div>
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold text-gray-900 truncate">
                      {reaction.user?.fullName || "Anonymous User"}
                    </h4>
                    {reaction.user?.role && (
                      <p className="text-xs text-gray-500 truncate capitalize">
                        {reaction.user.role.replace('_', ' ')}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-12 text-center text-gray-500 text-sm">
              No reactions yet
            </div>
          )}
        </div>
        
      </div>
    </div>,
    document.body
  );
};

export default ReactionModal;
